import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Flex, Text, useDisclosure, useToken } from "@chakra-ui/react";
import { ReactNode, createContext, useContext } from "react";

import { ContextMenu } from "@nens/chakra/components";
import { MenuAction, RanaAlert, RanaAlertProps, RanaContextMenu, RanaHeading } from "@nens/ranalib";

export interface StatusBannerProps {
  status: RanaAlertProps["status"];
  title: ReactNode;
  description?: ReactNode;
  timestamp?: ReactNode;
  actions?: MenuAction[];
  footer?: ReactNode;
  ariaLabel?: string;
}

const StatusBannerStackContext = createContext(false);

const getStatusBannerTransform = ({
  isInStack,
  isOpen,
  halfRoundBannerSize,
}: {
  isInStack: boolean;
  isOpen: boolean;
  halfRoundBannerSize: string;
}) => {
  const expandedBannerTransform = isInStack ? "translateX(0)" : "translateX(-100%)";
  const collapsedBannerTransform = isInStack
    ? `translateX(calc(100% - ${halfRoundBannerSize}))`
    : `translateX(-${halfRoundBannerSize})`;

  return isOpen ? expandedBannerTransform : collapsedBannerTransform;
};

export interface StatusBannerStackProps {
  children: ReactNode;
  gap?: string | number;
}

export function StatusBannerStack({ children, gap = 2 }: StatusBannerStackProps) {
  return (
    <StatusBannerStackContext.Provider value>
      <Flex
        position="fixed"
        top={16}
        right={0}
        flexDirection="column"
        alignItems="flex-end"
        gap={gap}
        zIndex={1000}
        pointerEvents="none"
      >
        {children}
      </Flex>
    </StatusBannerStackContext.Provider>
  );
}

export function StatusBanner({
  status,
  title,
  description,
  timestamp,
  actions = [],
  footer,
  ariaLabel = "Open status",
}: StatusBannerProps) {
  const isInStack = useContext(StatusBannerStackContext);
  const { isOpen, onToggle } = useDisclosure();
  const [halfRoundBannerSize] = useToken("sizes", ["6"]);
  const [baseBorderWidth] = useToken("borderWidths", ["base"]);

  return (
    <ContextMenu<HTMLDivElement>
      renderMenu={actions.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
    >
      {(ref) => (
        <Flex
          ref={ref}
          position={isInStack ? "relative" : "fixed"}
          left={isInStack ? undefined : "full"}
          top={isInStack ? undefined : 16}
          transform={getStatusBannerTransform({
            isInStack,
            isOpen,
            halfRoundBannerSize,
          })}
          transition="transform 0.2s ease-in-out"
          zIndex={isInStack ? undefined : 1000}
          pointerEvents="none"
        >
          <RanaAlert
            overrideIcon={isOpen ? ChevronRightIcon : ChevronLeftIcon}
            variant="icon-only"
            iconSize={6}
            status={status}
            roundedLeft="full"
            roundedRight="none"
            aria-label={ariaLabel}
            h={`calc(${halfRoundBannerSize} * 2)`}
            w={halfRoundBannerSize}
            minW={0}
            flexShrink={0}
            p={0}
            borderWidth="base"
            borderRightWidth="none"
            zIndex={1}
            position="relative"
            cursor="pointer"
            overflow="visible"
            onClick={onToggle}
            pointerEvents="auto"
          >
            {/* Small little empty box overlaps border of other alert so it looks like one element */}
            <Box
              bg="inherit"
              h="full"
              transform="translateX(50%)"
              position="absolute"
              zIndex={2}
              right={0}
              top={0}
              w={`calc(${baseBorderWidth} * 4)`}
              pointerEvents="none"
            />
          </RanaAlert>
          <RanaAlert
            borderRightWidth="none"
            borderRadius="none"
            borderBottomLeftRadius="base"
            status={status}
            p={4}
            visibility={isOpen ? "visible" : "hidden"}
            pointerEvents="auto"
          >
            <Flex whiteSpace="nowrap" flexDirection="column">
              <Flex w="full" justifyContent="space-between">
                <RanaHeading type="h4" mb={2} mr={4}>
                  {title}
                </RanaHeading>
                {actions.length > 0 && <RanaContextMenu actions={actions} />}
              </Flex>

              {description}
              {timestamp && (
                <Text fontStyle="italic" fontSize="sm" mb={4}>
                  {timestamp}
                </Text>
              )}
              {footer}
            </Flex>
          </RanaAlert>
        </Flex>
      )}
    </ContextMenu>
  );
}
